import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, AlertTriangle, Phone, Check } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Chip, Button, Skeleton, EmptyState, Toast } from "@/ui";

function Stars({ n }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={15} strokeWidth={1.5} fill={i <= n ? "var(--warn)" : "none"} color={i <= n ? "var(--warn)" : "var(--border-strong)"} />
      ))}
    </span>
  );
}

export default function Reviews() {
  const navigate = useNavigate();
  const [rating, setRating] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [rv, tk] = await Promise.all([api.get("/reviews"), api.get("/tasks")]);
      setRating(rv.data.rating);
      setTasks((tk.data.tasks || []).filter((t) => t.type === "poor_feedback"));
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/tasks/${id}/resolve`, {});
      setTasks((ts) => ts.filter((t) => t.id !== id));
      setToast("marked handled — nice save!");
      setTimeout(() => setToast(""), 2500);
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(""); }
  };

  return (
    <Layout>
      <div style={{ marginBottom: 8 }}>
        <h1 className="t-h1">reviews</h1>
        <p className="muted">what customers say after their order. handle an unhappy one fast and you keep them.</p>
      </div>

      {loading ? (
        <div className="stack"><Skeleton variant="card" /><Skeleton variant="card" /></div>
      ) : (
        <div className="stack" style={{ gap: 16 }}>
          {error ? <div className="err-text" data-testid="reviews-error">{error}</div> : null}

          {/* The complaint path — fast and obvious, first. */}
          {tasks.length > 0 ? (
            <Card data-testid="attention-card" className="stack" style={{ gap: 12, border: "2px solid #fecaca", background: "#fff7f7" }}>
              <div className="row" style={{ gap: 8 }}>
                <AlertTriangle size={18} className="text-rose-600" />
                <div className="text-lg font-semibold text-rose-700">Needs your attention</div>
                <Chip tone="bad">{tasks.length}</Chip>
              </div>
              {tasks.map((t) => (
                <div key={t.id} data-testid={`task-${t.id}`} className="rounded-xl p-4" style={{ background: "#fff", border: "1px solid var(--border)" }}>
                  <div className="row-between" style={{ marginBottom: 6 }}>
                    <Stars n={t.rating} />
                    <span className="muted-3 t-small">order #{(t.orderId || "").replace("order-", "")}</span>
                  </div>
                  <p className="t-body" style={{ marginBottom: 8 }}>&ldquo;{t.comment || "No comment given."}&rdquo;</p>
                  <div className="row-between" style={{ flexWrap: "wrap", gap: 8 }}>
                    <div className="t-small muted">
                      <strong>{t.customerName || "Customer"}</strong>
                      {t.customerPhone ? <span className="num"> · <Phone size={11} style={{ display: "inline" }} /> {t.customerPhone}</span> : null}
                      {t.order ? <span> · {t.order.items} ({t.order.gross})</span> : null}
                    </div>
                    <Button size="sm" variant="primary" data-testid={`resolve-${t.id}`} loading={busy === t.id} onClick={() => resolve(t.id)}>
                      <Check size={14} strokeWidth={2} />mark handled
                    </Button>
                  </div>
                </div>
              ))}
              <p className="muted-3 t-small">These are never shown publicly. Reach out, make it right, and mark it handled.</p>
            </Card>
          ) : null}

          {/* Public shop rating */}
          <Card data-testid="public-rating-card" className="stack" style={{ gap: 12 }}>
            <div className="row" style={{ gap: 8 }}>
              <div className="rounded-full p-2" style={{ background: "var(--sunk)" }}><Star size={18} className="text-amber-500" /></div>
              <div>
                <div className="text-lg font-semibold">Your public rating</div>
                <div className="text-sm text-slate-500">Shown on your shop page.</div>
              </div>
            </div>
            {rating && rating.count ? (
              <>
                <div className="row" style={{ gap: 12, alignItems: "center" }}>
                  <div className="text-4xl font-bold num" data-testid="public-rating-avg">{rating.average}</div>
                  <div><Stars n={Math.round(rating.average)} /><div className="muted-3 t-small">{rating.count} rating(s)</div></div>
                </div>
                <div className="stack" style={{ gap: 8 }}>
                  {(rating.reviews || []).map((r, i) => (
                    <div key={i} data-testid={`review-${i}`} className="rounded-xl px-3 py-2" style={{ background: "#f8fafc" }}>
                      <Stars n={r.rating} />
                      {r.comment ? <p className="t-small" style={{ marginTop: 4 }}>&ldquo;{r.comment}&rdquo;</p> : null}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <EmptyState icon={<Star size={20} />} title="no public ratings yet — they'll appear here once customers rate a delivered order" />
            )}
          </Card>
        </div>
      )}
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="reviews-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
